import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Bell, AlertCircle } from 'lucide-react';
import { reminders as remindersApi, type Reminder } from '../lib/api';
import { useAuth } from '../lib/auth';
import ReminderCard from '../components/ReminderCard';
import AdBanner from '../components/AdBanner';

type Filter = 'all' | 'pending' | 'completed';

export default function Dashboard() {
  const { user } = useAuth();
  const [items, setItems] = useState<Reminder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState<Filter>('all');

  const load = useCallback(async () => {
    try {
      const { reminders } = await remindersApi.list();
      setItems(reminders);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this reminder?')) return;
    try {
      await remindersApi.remove(id);
      setItems(prev => prev.filter(r => r.id !== id));
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleToggle = async (id: string) => {
    try {
      await remindersApi.toggleComplete(id);
      setItems(prev => prev.map(r => r.id === id ? { ...r, is_completed: r.is_completed ? 0 : 1 } : r));
    } catch (err: any) {
      setError(err.message);
    }
  };

  const filtered = items.filter(r => {
    if (filter === 'pending') return !r.is_completed;
    if (filter === 'completed') return r.is_completed;
    return true;
  });

  const activeCount = items.filter(r => !r.is_completed).length;
  const canAdd = items.length < 5;

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">My Reminders</h1>
          <p className="text-gray-500 text-sm mt-0.5">
            {items.length}/5 reminders &bull; {activeCount} pending
          </p>
        </div>
        {canAdd ? (
          <Link to="/reminders/new" className="btn-primary flex items-center gap-2">
            <Plus className="w-4 h-4" /> Add Reminder
          </Link>
        ) : (
          <div className="text-sm text-amber-600 bg-amber-50 px-3 py-2 rounded-lg font-medium">
            Max 5 reached
          </div>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2 text-sm">
          <AlertCircle className="w-4 h-4 flex-shrink-0" /> {error}
        </div>
      )}

      {/* Filter tabs */}
      {items.length > 0 && (
        <div className="flex gap-1 bg-gray-100 p-1 rounded-lg w-fit">
          {(['all', 'pending', 'completed'] as Filter[]).map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 py-1.5 rounded-md text-sm font-medium capitalize transition-colors ${
                filter === f ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {f}
            </button>
          ))}
        </div>
      )}

      {/* Reminders list */}
      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="card animate-pulse h-24 bg-gray-100" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="card text-center py-16 space-y-4">
          <Bell className="w-12 h-12 text-gray-300 mx-auto" />
          <p className="text-gray-400 font-medium">
            {filter !== 'all' ? `No ${filter} reminders` : 'No reminders yet'}
          </p>
          {filter === 'all' && canAdd && (
            <Link to="/reminders/new" className="btn-primary inline-flex items-center gap-2">
              <Plus className="w-4 h-4" /> Add your first reminder
            </Link>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {filtered.map(r => (
            <ReminderCard key={r.id} reminder={r} onDelete={handleDelete} onToggle={handleToggle} />
          ))}
        </div>
      )}

      {/* Ad */}
      <AdBanner slot="2222222222" format="rectangle" className="min-h-[250px] bg-gray-100 rounded-xl" />
    </div>
  );
}
